// "Hand off to Agent" in the "…" of a Case's step: the Action becomes a child
// chat of the Case's chat (made when the Case has none), and the screen moves
// to that chat. The host gives this to `casesScreen({ stepMenu })`; the Case
// screen does not import this part, nor this part the Case screen's code.
//
// Promise:
// - Only an open current Action that is not handed off yet offers it (what the
//   server accepts), and only on a screen that signs (the app's); once handed
//   off, the row opens the chat instead.
// - The write is the Case's own update (`delegate_steps`), so a refusal is one
//   line under the step's row and nothing moves.
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { AgentDeviceDTO } from "@trameria/core/agent-chat-shared";
import type { CaseDetail } from "@trameria/core/cases-shared";
import { m } from "@trameria/i18n";
import type { Signed } from "@trameria/shared-agent/instruction";
import { useApi } from "../app/context";
import type { UseStepMenuItems } from "../cases/extensions";
import type { Api } from "../data/api";
import {
  chatForSigning,
  type InstructionSigner,
  signOne,
  useSigner,
} from "./signing";

const chatKeyOf = (ext: Readonly<Record<string, unknown>> | undefined) => {
  const agent = ext?.agent as { key?: unknown } | undefined;
  return typeof agent?.key === "string" ? agent.key : null;
};

/**
 * The delegation signed for the device its chats run on (the Case's chat's,
 * else this PC's): undefined when the screen has no signer.
 */
async function signDelegation(
  signer: InstructionSigner | null,
  qc: QueryClient,
  api: Api,
  caseId: string,
): Promise<Signed | undefined> {
  if (!signer) return undefined;
  const detail = await api<CaseDetail>(
    `/api/me/cases/${encodeURIComponent(caseId)}`,
  );
  const consult = (detail.ext?.agent as { id?: unknown } | undefined)?.id;
  const device =
    typeof consult === "string"
      ? (await chatForSigning(qc, api, consult)).deviceId
      : ((
          await api<{ items: AgentDeviceDTO[] }>("/api/me/agents/devices")
        ).items.find((d) => d.kind === "this_pc")?.id ?? null);
  return signOne(signer, {
    device,
    chat: null,
    kind: "delegate",
    content: {
      case: detail.id,
      from: null,
      restricted: false,
      conditions: null,
    },
  });
}

export const useDelegateStepMenu: UseStepMenuItems = ({
  caseId,
  step,
  write,
}) => {
  const navigate = useNavigate();
  const signer = useSigner();
  const api = useApi();
  const qc = useQueryClient();
  const open = (key: string) =>
    void navigate({ to: "/me/agents/$key", params: { key } });
  const key = chatKeyOf(step.ext);
  if (key)
    return [
      {
        id: "agent-chat.open",
        label: m.agents_delegate_step_open(),
        onSelect: () => open(key),
      },
    ];
  // A screen without the app's signer (a browser) cannot hand off: the agent takes signed instructions only.
  if (
    !signer ||
    step.kind !== "action" ||
    step.status !== "open" ||
    !step.current
  )
    return [];
  return [
    {
      id: "agent-chat.delegate",
      label: m.agents_delegate_step(),
      onSelect: () =>
        void signDelegation(signer, qc, api, caseId)
          .then((signed) =>
            write(
              {
                delegate_steps: [{ id: step.id }],
                ...(signed ? { delegate_signed: signed } : {}),
              },
              m.agents_delegate_step_failed({ title: step.title }),
            ),
          )
          .then(
            (detail) => {
              const made = (detail as CaseDetail).steps.find(
                (s) => s.id === step.id,
              );
              const chat = chatKeyOf(made?.ext);
              if (chat) open(chat);
            },
            // Told under the step's row by the Case screen.
            () => {},
          ),
    },
  ];
};
